/**
 * Step Data Scripts
 *
 * Displays cached data script results from workflow step execution.
 * Shows script label, status, and stdout/stderr in a compact format.
 *
 * @module FlowDesigner/runs/StepDataScripts
 */
import React, { useState } from 'react';
import { Code, CaretDown, CaretRight, CheckCircle, XCircle } from '@phosphor-icons/react';
import { Text } from '@chatsdet/ui';
import { CopyablePre } from '../../../components/CopyButton';

export interface DataScriptResult {
  label: string;
  success: boolean;
  stdout?: string;
  stderr?: string;
  error?: string;
  durationMs: number;
}

export function StepDataScripts({ results }: { results: DataScriptResult[] }) {
  if (results.length === 0) return null;

  return (
    <div className="ml-4 mt-1 space-y-1">
      {results.map((r) => (
        <DataScriptEntry key={r.label} result={r} />
      ))}
    </div>
  );
}

function DataScriptEntry({ result }: { result: DataScriptResult }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium transition-colors cursor-pointer bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/30"
      >
        {result.success ? (
          <CheckCircle size={12} weight="bold" className="text-green-600 dark:text-green-400" />
        ) : (
          <XCircle size={12} weight="bold" className="text-red-600 dark:text-red-400" />
        )}
        <Code size={12} weight="bold" />
        <span className="truncate max-w-[200px]">{result.label}</span>
        <span className="text-violet-500 dark:text-violet-400 text-[10px]">
          {result.durationMs}ms
        </span>
        {expanded ? <CaretDown size={10} /> : <CaretRight size={10} />}
      </button>

      {expanded && (
        <div className="ml-4 mt-1 space-y-1">
          {result.stdout && (
            <CopyablePre value={result.stdout} label="stdout">
              <pre className="text-[10px] leading-tight bg-gray-50 dark:bg-gray-800 rounded p-2 overflow-x-auto max-h-32 text-gray-700 dark:text-gray-300">
                {result.stdout}
              </pre>
            </CopyablePre>
          )}
          {result.stderr && (
            <CopyablePre value={result.stderr} label="stderr">
              <pre className="text-[10px] leading-tight bg-red-50 dark:bg-red-900/20 rounded p-2 overflow-x-auto max-h-32 text-red-700 dark:text-red-300">
                {result.stderr}
              </pre>
            </CopyablePre>
          )}
          {result.error && !result.stderr && (
            <Text size="xs" className="text-destructive">
              {result.error}
            </Text>
          )}
        </div>
      )}
    </div>
  );
}
